import dayjs from 'dayjs';
import { createSelector } from 'reselect';

import { IProps as IPriceProps } from '@/components/switchback/Price/Price';
import { TRootState } from '@/redux/rootReducer';
import { getCurrency } from '@/redux/selectors/currency';
import { getFromAndTo } from '@/redux/selectors/queryParams';
import { IWaiverSummary } from '@/services/types/core/quotes';
import { formatCurrency } from '@/utility/currency';
import formatDate from '@/utility/format-date';
import { isSSR } from '@/utility/isSSR';
import { mapRentalToPrice } from '@/utility/mapSearchResultToTile';
import { IOutdoorsySessionCookie } from '@/utility/session';

import { getListingPresentmentCurrency, getListingSocialImage, PriceType } from './page';

type TQuoteData = TRootState['quote']['data'];
type TListingData = TRootState['listing']['data'];

export interface IBillSummary {
  addonsValue?: string;
  addonsQuantity?: number;
  feesValue: string;
  rawFeesValue?: number;
  deliveryValue?: string;
  insuranceValue?: string;
  insuranceBundleId?: string;
  duration: number;
  totalPerDay: string;
  totalValue: string;
  rawTotalValue?: string;
  credit?: string;
  discount?: {
    code: string;
    amount: string;
  };
  isCustomInsurance: boolean;
  ownerName?: string;
  tripInsurance?: string;
  damageProtection?: string;
  waiverSummary?: IWaiverSummary;
  dataOrigin?: 'booking' | 'quote';
}

export interface IQuoteEventData {
  event: string;
  rental_id?: number;
  product_id?: number;
  sku?: number;
  category?: string;
  name?: string;
  sleeps?: number;
  price: number;
  price_per_night: number;
  total: number;
  url?: string;
  image_url?: string;
  from?: string;
  to?: string;
  instant_book?: boolean;
  isOutdoorsyStay?: boolean;
}

export const getQuoteFromAndTo = (state: TRootState) => {
  if (!state.quote.data) {
    return {};
  }

  return {
    from: state.quote.data?.from,
    to: state.quote.data?.to,
  };
};

export const getQuoteFromAndToTimes = (state: TRootState) => {
  if (!state.quote.data) {
    return {};
  }

  return {
    fromTime: state.quote.data?.from_time,
    toTime: state.quote.data?.to_time,
  };
};

export const getQuoteInput = (state: TRootState) => {
  const currency = getCurrency(state);
  const prevQuote = state.quote.data;
  const discountCode =
    prevQuote?.discount_code || state.cookies.discountCode || state.queryParams.discount_code;
  const rentalId = state.listing.data?.id;
  const rentalItems = state.listing.data?.items;
  const authToken = (state.cookies['outdoorsy-session'] as IOutdoorsySessionCookie)?.authenticated
    ?.token;

  const items = rentalItems
    ?.filter(item => item.count)
    .map(({ id, count }) => ({
      id,
      count,
    }));

  return {
    currency,
    discountCode,
    rentalId,
    items,
    authToken,
  };
};

export const getTotal = createSelector<
  TRootState,
  TQuoteData,
  ReturnType<typeof getCurrency>,
  string
>(
  state => state.quote.data,
  getCurrency,
  (quote, currency) =>
    formatCurrency({
      priceInCents: quote ? quote.total - (quote?.renter_credits_applied ?? 0) : 0,
      currency,
      digits: 2,
    }),
);

type ISiblingPriceProps = IPriceProps;

interface IPricingInformation extends IPriceProps {
  from?: string;
  to?: string;
  fromTime?: number;
  toTime?: number;
  sibling?: ISiblingPriceProps;
  isQuotePending?: boolean;
}

// MP-7078: `state.quote.data` is a single mutable slot. When the URL date
// window changes (e.g. user picks new dates on the listing page), the OLD
// quote lingers in Redux until the new dated quote lands. Reading the stale
// quote's day price in that window is the drip-pricing bug. Consumers must
// gate on this before showing a numeric price or enabling Reserve.
//
// Both sides are normalized through the exact same YYYY-MM-DD formatter
// `getQuote` uses to build its POST body (see `redux/modules/quote.ts:372`),
// because the URL date can arrive as an ISO datetime (one-way search widget
// writes `d.toISOString()`) or as a plain calendar date (regular SRP hand-off)
// while `quote.from`/`to` are always calendar dates from java-api. String
// equality without normalization would leave one-way listings permanently
// out of sync — endless shimmer, disabled Reserve.
const normalizeQuoteDate = (d?: string): string | undefined => {
  if (!d) return undefined;
  const parsed = dayjs(d);
  return parsed.isValid() ? formatDate(parsed, 'YYYY-MM-DD') : undefined;
};

export const getIsQuoteInSyncWithDates = createSelector(
  getFromAndTo,
  (state: TRootState) => state.quote.data,
  (state: TRootState) => state.quote.errorMessage,
  (queryDates, quote, quoteErrorMessage) => {
    const nQueryFrom = normalizeQuoteDate(queryDates.from);
    const nQueryTo = normalizeQuoteDate(queryDates.to);
    if (!nQueryFrom || !nQueryTo) return true;
    // A settled quote error is a terminal state — no shimmer is going to
    // resolve into a valid quote. Report "in sync" so consumers render the
    // error / "Change dates" branch instead of a permanent skeleton with a
    // disabled recovery CTA.
    if (quoteErrorMessage) return true;
    const nQuoteFrom = normalizeQuoteDate(quote?.from);
    const nQuoteTo = normalizeQuoteDate(quote?.to);
    if (!nQuoteFrom || !nQuoteTo) return false;
    return nQuoteFrom === nQueryFrom && nQuoteTo === nQueryTo;
  },
);

const calcDiscount = (originalPrice: number, currentPrice: number) =>
  Math.round(((originalPrice && 1 - currentPrice / originalPrice) || 0 + Number.EPSILON) * 100) ||
  0;

export const getPricingInformation = createSelector<
  TRootState,
  TQuoteData,
  TListingData | undefined,
  ReturnType<typeof getListingPresentmentCurrency>,
  boolean,
  IPricingInformation
>(
  state => state.quote.data,
  state => state.listing.data,
  getListingPresentmentCurrency,
  getIsQuoteInSyncWithDates,
  (quote, listing, listingCurrency, isInSyncWithDates) => {
    // MP-7078: When the URL has dates but our quote is either absent or was
    // fetched for a different window, treat this as "quote pending". Consumers
    // must render a loading state instead of the stale/base numeric price.
    const isQuotePending = !isInSyncWithDates;

    if (!quote) {
      const price = mapRentalToPrice({ rental: listing });
      return {
        currency: listingCurrency,
        ...price,
        isQuotePending,
      } as IPricingInformation;
    }

    const instantBook = quote.instant_book && quote.is_request_less_than_minimum_days !== true;
    const { from, to, from_time: fromTime, to_time: toTime, presentment_currency } = quote;
    const originalPrice = quote.original_day_price;
    const currentPrice = quote.calculated_day_price;
    const overrideDayPrice = quote.original_day_override_price || originalPrice;
    const discount = calcDiscount(originalPrice, currentPrice);
    const discountFromOverrideDayPrice = calcDiscount(overrideDayPrice, currentPrice);
    const sibling = quote.siblings?.rental_campsite;
    const siblingCurrentPrice = sibling?.calculated_day_price;
    const siblingOriginalPrice = sibling?.original_day_price;
    const siblingOverrideDayPrice =
      sibling?.original_day_override_price || sibling?.original_day_price;
    const siblingDiscountFromOverrideDayPrice =
      siblingOverrideDayPrice && siblingCurrentPrice
        ? calcDiscount(siblingOverrideDayPrice, siblingCurrentPrice)
        : 0;

    return {
      currency: presentment_currency,
      instantBook,
      currentPrice,
      originalPrice,
      overrideDayPrice,
      discount,
      discountFromOverrideDayPrice,
      period: listing?.active_options?.use_day_pricing ? PriceType.DAY : PriceType.NIGHT,
      from,
      to,
      fromTime,
      toTime,
      isQuotePending,
      ...(sibling && {
        sibling: {
          currency: presentment_currency,
          period: PriceType.NIGHT,
          instant_book: sibling?.instant_book,
          currentPrice: siblingCurrentPrice,
          original_price: siblingOriginalPrice,
          overrideDayPrice: siblingOverrideDayPrice,
          discountFromOverrideDayPrice: siblingDiscountFromOverrideDayPrice,
        },
      }),
    } as IPricingInformation;
  },
);

export const getCheckoutURL = createSelector<
  TRootState,
  number | undefined,
  string | undefined,
  string | string[] | undefined,
  string
>(
  state => state.listing.data?.id,
  state => state.quote.data?.id,
  state => state.queryParams.renter_age,
  (rentalId, quoteId, renterAge) => {
    const baseURL = `/checkout/${rentalId ?? ''}/new`;
    const queryParams = new URLSearchParams();

    if (quoteId) {
      queryParams.set('quote', quoteId);
    }

    if (renterAge) {
      const ageValue = Array.isArray(renterAge) ? renterAge[0] : renterAge;
      queryParams.set('renter_age', String(ageValue));
    }

    const queryString = queryParams.toString();
    return queryString ? `${baseURL}?${queryString}` : baseURL;
  },
);

export const getIsInstantBook = (state: TRootState) =>
  Boolean(
    (state.listing.data?.instant_book || state.checkout.booking?.instant_book) &&
      state.quote.data?.is_request_less_than_minimum_days !== true,
  );

export const getIsRequestLessThanMinimumDays = (state: TRootState) =>
  Boolean(state.quote.data?.is_request_less_than_minimum_days === true);

export const getDiscountMessage = (state: TRootState) =>
  state.quote.data?.discount_message?.message;

export const getQuoteDuration = (state: TRootState) => state.quote.data?.duration;

export const getQuoteEventData = createSelector<
  TRootState,
  TListingData,
  TQuoteData,
  ReturnType<typeof getFromAndTo>,
  ReturnType<typeof getListingSocialImage>,
  IQuoteEventData
>(
  state => state.listing.data,
  state => state.quote.data,
  getFromAndTo,
  getListingSocialImage,
  (listing, quote, selectedDates, image) => {
    const rental_category = 'rv';

    return {
      event: 'Quote loaded',
      rental_category,
      rental_id: listing?.id,
      product_id: listing?.id,
      sku: listing?.id,
      category: listing?.type,
      name: listing?.name,
      sleeps: listing?.sleeps,
      price: (quote?.original_day_price || 0) / 100,
      price_per_night: (quote?.calculated_day_price || 0) / 100,
      total: ((quote?.total_after_credits ?? quote?.total) || 0) / 100,
      url: !isSSR() ? window.location.href : undefined,
      image_url: image,
      from: selectedDates.from || quote?.from,
      to: selectedDates.to || quote?.to,
      instant_book: listing?.instant_book && quote?.is_request_less_than_minimum_days !== true,
      isOutdoorsyStay: Boolean(quote?.sibling_quote_group_id),
    };
  },
);

export const getQuoteOriginalDayPrice = createSelector<TRootState, TQuoteData, number | undefined>(
  state => state.quote.data,
  quote => (quote?.original_day_price || 0) / 100,
);

export const getQuoteDiscountPercentOff = createSelector<
  TRootState,
  TQuoteData,
  number | undefined
>(
  state => state.quote.data,
  quote => {
    const originalPrice = quote?.original_day_price || 0;
    const currentPrice = quote?.calculated_day_price || 0;
    const discount =
      Math.round(
        ((originalPrice && 1 - currentPrice / originalPrice) || 0 + Number.EPSILON) * 100,
      ) || 0;
    return discount;
  },
);
